import { isApiRequestConfig } from '../utils/apiAuthRecovery';
import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { signOut } from "firebase/auth";
import { auth } from "../firebaseAuth";
import {
  createApiAuthResponseErrorHandler,
  API_SESSION_EXPIRED_EVENT,
} from "../utils/apiAuthRecovery";
import { resolveFrontendApiBase } from "../utils/apiBase";
import { getAuthHeaders } from "../utils/authHeaders";
import { AUTH_STAGE_TIMEOUT_MS, runAuthStage } from "../utils/authTelemetry";
import { resolveAgentPlatformApiOrigin } from "./configuration";

const configuredOriginValue = String(
  process.env.REACT_APP_AGENT_PLATFORM_API_ORIGIN || ""
).trim();
const configuredOrigin = resolveAgentPlatformApiOrigin();
const legacyApiBase = `${resolveFrontendApiBase()}/api/agent-platform`;
const API_BASE = configuredOrigin
  ? `${configuredOrigin}/api/agent-platform`
  : legacyApiBase;
const dedicatedClient: AxiosInstance | undefined = configuredOrigin
  ? axios.create({ baseURL: API_BASE, withCredentials: true })
  : undefined;

if (dedicatedClient) {
  dedicatedClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (!isApiRequestConfig(config, API_BASE)) throw new Error('Request target is outside the configured API.');
      const headers = AxiosHeaders.from(config.headers);
      const authHeaders = await getAuthHeaders(auth.currentUser);
      Object.entries(authHeaders).forEach(([key, value]) => headers.set(key, value));
      config.headers = headers;
      return config;
    }
  );
  dedicatedClient.interceptors.response.use(
    (response) => response,
    createApiAuthResponseErrorHandler({
      apiBase: API_BASE,
      getCurrentUser: () => auth.currentUser,
      replayRequest: (config) =>
        dedicatedClient.request(config as InternalAxiosRequestConfig),
      setAuthorizationHeader: (config, token) => {
        const headers = AxiosHeaders.from(config.headers || {});
        headers.set("Authorization", `Bearer ${token}`);
        config.headers = headers;
      },
      terminateSession: async () => {
        if (typeof window !== "undefined")
          window.dispatchEvent(new CustomEvent(API_SESSION_EXPIRED_EVENT));
        await runAuthStage("session_sign_out", () => signOut(auth), {
          timeoutMs: AUTH_STAGE_TIMEOUT_MS.signOut,
        });
      },
    })
  );
}

export type Workspace = {
  id: string;
  name: string;
  workspaceType: string;
  timezone: string;
};
export type OwnerAgentId = "chief_of_staff" | "support" | "engineering";
export type ResourceBinding = { resourceId: string; capabilities: string[] };
export type OwnerAgent = {
  id: OwnerAgentId;
  name: string;
  storageWorkspaceIds: string[];
  resourceBindings: ResourceBinding[];
  configurationRevision: number;
};
export type OwnerResource = {
  id: string;
  type: string;
  displayName: string;
  connectionRef: string;
  ownerUserId: string;
  capabilities: string[];
  enabled: boolean;
  workspaceId: string;
};
export type OwnerItemDetail = {
  summary?: string;
  findings?: Array<{ text: string; evidenceIds?: string[] }>;
  recommendation?: string;
  evidence?: SupportEvidence[];
  affectedFiles?: Array<{ path: string; reason: string; evidenceIds: string[] }>;
  proposedChange?: string;
  regressionTests?: string[];
  fixCaseDraft?: { id: string; status: "draft_for_review"; title: string; summary: string; proposedChange: string; regressionTests: string[]; engineeringTaskId: string; limitation: string } | null;
  codeDefectStatus?: "found" | "not_found" | "inconclusive";
  supportFindings?: Array<{ text: string; evidenceIds: string[] }>;
  reproductionRequests?: Array<{ operation?: "translation" | "proofreading"; bookId: string; chapterId: string; sourceLang: string;
    targetLangs: string[]; repeats: number; paragraphIndex?: number; reason: string }>;
  observedTests?: Array<{ status: "completed" | "failed" | "cancelled"; summary: string; evidenceIds: string[] }>;
  refundRecommendation?: { decision: "none" | "review_eligibility" | "recommend_refund";
    reason: string; evidenceIds: string[] } | null;
  reproductionId?: string;
  reproductionStatus?: string;
  draftText?: string;
};
export type OwnerItem = {
  id: string;
  kind: "task" | "support_case" | "engineering_task";
  agent: OwnerAgentId;
  title: string;
  status: string;
  attentionState?: string | null;
  createdAt: string;
  updatedAt: string;
  nextCheckAt?: string | null;
  sourceRefs: Array<Pick<SupportEvidence, "id" | "kind" | "title" | "url"> & {
    path?: string; startLine?: number; endLine?: number;
  }>;
  workspaceId: string;
  detail?: OwnerItemDetail;
};
export type OwnerActivity = Omit<OwnerItem, "kind" | "attentionState" | "nextCheckAt" | "detail"> & {
  kind: "brief" | "run" | "approval";
};
export type OwnerPlatform = {
  agents: OwnerAgent[];
  resources: OwnerResource[];
  items: OwnerItem[];
  activity: OwnerActivity[];
  workspaces: Workspace[];
  readOnly: boolean;
};
export type Agent = {
  id: string;
  name?: string;
  displayName?: string;
  status?: string;
  agentType?: string;
};
export type Run = {
  id: string;
  status: string;
  agentId?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  boundedSummary?: string | null;
  errorCode?: string | null;
};
export type ApprovalProposal = {
  title: string;
  summary: string;
  toolName: string;
  targetId: string;
};
export type ConfirmedNote = { id: string; note: string; createdAt: string };
export type Approval = {
  id: string;
  runId: string;
  status: string;
  proposalVersion: string | number;
  proposalHash: string;
  expiresAt: string | null;
  proposal: ApprovalProposal;
  confirmedNote: ConfirmedNote | null;
};
export type BriefStatus = "queued" | "running" | "succeeded" | "failed";
export type BriefSourceKind = "gmail" | "drive" | "calendar" | "web";
export type BriefSource = {
  id: string;
  kind: BriefSourceKind;
  title: string;
  url: string;
  accountIdentifier: string;
};
export type BriefSection = {
  title: string;
  items: Array<{ text: string; sourceIds: string[] }>;
};
export type Brief = {
  id: string;
  workspaceId: string;
  status: BriefStatus;
  createdAt: string;
  completedAt?: string | null;
  errorCode?: string | null;
  summary?: string | null;
  sections?: BriefSection[];
  sources?: BriefSource[];
  coverage?: string[];
  usage?: { inputTokens: number; outputTokens: number };
  attentionRequired?: boolean;
};
export type IssueStatus =
  | "open"
  | "waiting"
  | "needs_user"
  | "resolved"
  | "dismissed";
export type IssueAction = "resolve" | "dismiss" | "reopen" | "snooze" | "acknowledge";
export type Issue = {
  id: string;
  workspaceId: string;
  status: IssueStatus;
  revision: number;
  title: string;
  detail: string;
  sourceIds: string[];
  sources?: BriefSource[];
  accountId: string;
  threadId: string;
  createdAt: string;
  updatedAt: string;
  lastObservedAt?: string;
  snoozeUntil?: string;
  attentionState?: "needs_attention" | "watching" | "waiting_on_you" | "resolved";
  attentionVersion?: number;
  acknowledgedVersion?: number;
  stale?: boolean;
  lastCheckedAt?: string;
  nextCheckAt?: string;
};
export type Evaluation = { enabled: boolean; window: { firstRunAt?: string; lastRunAt?: string; runCount: number }; runs: Record<string, number>; usage: { knownRuns: number; unknownRuns: number; inputTokens: number; outputTokens: number }; reliability: { explicitToolDenialRuns: number; unclassifiedFailedRuns: number; unchangedReminders: number }; issues: Record<string, number>; ownerLabels: Record<string, number> };
export type MonitoringMission = {
  schemaVersion: 1;
  missionVersion: number;
  status: "active" | "paused" | "waiting_quota" | "stopped_reconciliation";
  intervalSeconds: number;
  objective: "household_attention_brief" | "spark3d_operations_attention";
  nextCheckAt: string;
  lastCheckAt?: string;
  lastBriefId?: string;
  heartbeatAt?: string;
  pauseReason?: "owner_paused" | "quota_backoff" | "model_outcome_unknown";
  updatedAt: string;
};
export type GoogleCapability =
  | "gmail.read"
  | "calendar.read"
  | "calendar.availability"
  | "drive.read";
export type GoogleConnectionDomain = "personal" | "family" | "work";
export type GoogleConnectedAccount = {
  id: string;
  workspaceId: string;
  provider: "google";
  accountIdentifier: string;
  displayName?: string | null;
  domain: GoogleConnectionDomain;
  status: string;
  requestedCapabilities: string[];
  grantedCapabilities: string[];
  missingCapabilities: string[];
  grantedScopes: string[];
  tokenExpiresAt?: string | null;
  lastErrorCode?: string | null;
  consentVersion: number;
};
export type SupportCandidate = {
  id: string;
  accountIdentifier: string;
  senderLabel: string;
  subject: string;
  observedAt: string;
  expiresAt: string;
};
export type SupportCaseStatus =
  | "queued" | "investigating" | "triaged" | "draft_ready"
  | "approved_for_copy" | "rejected" | "escalated" | "resolved" | "failed"
  | "waiting_customer" | "skipped" | "development_pending" | "development_investigating";
export type SupportCaseDisposition =
  | "draft" | "skip" | "waiting_customer" | "resolved" | "fix_sent" | "escalate_development";
export type SupportReviewAction =
  | "triage" | "save_draft" | "approve_for_copy" | "reject" | "escalate" | "resolve";
export type SupportEvidence = {
  id: string;
  kind: "gmail" | "support_article" | "internal_policy" | "promotion" | "transaction" | "documentation" | "product_record" | "customer" | "book" | "job" | "support_event" | "repository";
  title: string;
  url?: string;
  observedAt?: string;
  summary?: string;
};
export type SupportCustomerMatchReason =
  | "exact_sender_email"
  | "exact_book_title"
  | "support_code_owner";
export type SupportCustomerMatch = {
  status: "matched" | "ambiguous" | "unmatched";
  candidates: Array<{
    customerId: string;
    reasons: SupportCustomerMatchReason[];
    confidence?: "high" | "medium" | "low";
  }>;
  observedAt: string;
  requiresOperatorConfirmation?: boolean;
  conflictingEvidence?: boolean;
  coverage?: { bounded: boolean; returned: number; truncated: boolean };
};
export type SupportDevelopment = {
  taskId: string;
  status: "pending" | "queued" | "investigating" | "waiting_quota" | "waiting_active" | "waiting_reproduction" | "reproduction_review_required" | "findings_ready" | "failed";
  summary?: string;
  findings?: Array<{ text: string; evidenceIds: string[] }>;
  affectedFiles?: Array<{ path: string; reason: string; evidenceIds: string[] }>;
  proposedChange?: string;
  regressionTests?: string[];
  fixCaseDraft?: { id: string; status: "draft_for_review"; title: string; summary: string; proposedChange: string; regressionTests: string[]; engineeringTaskId: string; limitation: string } | null;
  codeDefectStatus?: "found" | "not_found" | "inconclusive";
  supportFindings?: Array<{ text: string; evidenceIds: string[] }>;
  reproductionRequests?: Array<{ operation?: "translation" | "proofreading"; bookId: string; chapterId: string; sourceLang: string;
    targetLangs: string[]; repeats: number; paragraphIndex?: number; reason: string }>;
  observedTests?: Array<{ status: "completed" | "failed" | "cancelled"; summary: string; evidenceIds: string[] }>;
  refundRecommendation?: { decision: "none" | "review_eligibility" | "recommend_refund";
    reason: string; evidenceIds: string[] } | null;
  reproductionId?: string;
  reproductionStatus?: string;
  evidence?: SupportEvidence[];
  updatedAt: string;
};
export type ReproductionRequestPayload = {
  operation?: "translation" | "proofreading";
  bookId: string; chapterId: string; sourceLang: string; targetLangs: string[];
  model: string; repeats: number; paragraphIndex: number; expectedCaseRevision: number;
};
export type ReproductionPreview = ReproductionRequestPayload & {
  caseId: string; workspaceId: string; planHash: string; sourceVersion: string;
  route: { project: string; worker: string; region: string; adminUserId: string };
  totalRuns: number; effects: string[]; limitations: string[];
};

export type SupportAutomation = {
  enabled: boolean;
  connected: boolean;
  lastSyncedAt?: string;
  lastHistoryId?: string;
  pendingIntakeCount: number;
  budgetWaitingCount: number;
  lastErrorCode?: string;
};
export type SupportCase = {
  id: string;
  workspaceId: string;
  status: SupportCaseStatus;
  revision: number;
  createdAt: string;
  updatedAt: string;
  source: { accountIdentifier: string; senderLabel?: string; subject?: string;
    replyFromAddress?: "contact@scribeshadow.com" | "support@scribeshadow.com"; observedAt: string };
  customerBinding: { status: "verified" | "unverified"; customerId?: string; verificationMethod?: "exact_sender_email" };
  customerMatch?: SupportCustomerMatch;
  category?: string;
  findings?: Array<{ text: string; evidenceIds: string[] }>;
  evidence?: SupportEvidence[];
  uncertainty?: string;
  capabilityGaps?: string[];
  chapterRemediation?: string;
  recommendation?: string;
  draft?: { version: number; text: string; approvalStatus?: string;
    provenance?: "human_internal" | "assistant_internal"; contentDigest?: string;
    evidenceRefs?: string[]; unresolvedAssertions?: string[] };
  draftReplacementProposal?: { version: number; text: string; provenance?: string;
    contentDigest?: string; evidenceRefs?: string[]; unresolvedAssertions?: string[] };
  lifecycle?: { version: string; phase: string; closed: boolean; diagnosticEligible: boolean;
    closureKind: string | null; blockers: string[]; nextResponsibleActor: string;
    replyIsRecoveryEvidence: false };
  review?: { action: SupportReviewAction; at: string; reasonCode?: string };
  errorCode?: string;
  resolutionReviewRequired?: boolean;
  closureReason?: "fix_sent";
  resolutionEvidenceId?: string;
  intakeKind?: "manual" | "automatic";
  disposition?: SupportCaseDisposition;
  development?: SupportDevelopment;
};

export class AgentPlatformApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "AgentPlatformApiError";
  }
}

async function request<T>(
  path: string,
  method: "get" | "post" = "get",
  data?: unknown,
  signal?: AbortSignal
): Promise<T> {
  if (configuredOriginValue && !configuredOrigin) {
    throw new AgentPlatformApiError(
      0,
      "The Agent Platform API origin is not configured safely for this dashboard."
    );
  }
  try {
    const response = dedicatedClient
      ? method === "get"
        ? await dedicatedClient.get<T>(path, { signal })
        : await dedicatedClient.post<T>(path, data, { signal })
      : method === "get"
      ? await axios.get<T>(`${API_BASE}${path}`, { signal })
      : await axios.post<T>(`${API_BASE}${path}`, data, { signal });
    return response.data;
  } catch (error: any) {
    if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") throw error;
    throw new AgentPlatformApiError(
      error?.response?.status || 0,
      error?.response?.data?.detail || "The request could not be completed."
    );
  }
}

const workspacePath = (workspaceId: string) =>
  `/workspaces/${encodeURIComponent(workspaceId)}`;
export const agentPlatformApi = {
  owner: (signal?: AbortSignal) =>
    request<OwnerPlatform>("/owner", "get", undefined, signal),
  updateChiefOfStaffResources: (
    expectedRevision: number,
    resourceBindings: ResourceBinding[]
  ) => request<{ agent: OwnerAgent }>("/owner/agents/chief_of_staff/resources", "post", {
    expectedRevision,
    resourceBindings,
  }),
  workspaces: (signal?: AbortSignal) =>
    request<{ items: Workspace[] }>(
      "/workspaces?limit=50",
      "get",
      undefined,
      signal
    ),
  agents: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: Agent[] }>(
      `${workspacePath(workspaceId)}/agents?limit=50`,
      "get",
      undefined,
      signal
    ),
  runs: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: Run[] }>(
      `${workspacePath(workspaceId)}/runs?limit=50`,
      "get",
      undefined,
      signal
    ),
  run: (workspaceId: string, runId: string, signal?: AbortSignal) =>
    request<{ run: Run }>(
      `${workspacePath(workspaceId)}/runs/${encodeURIComponent(runId)}`,
      "get",
      undefined,
      signal
    ),
  approvals: (workspaceId: string, runId: string, signal?: AbortSignal) =>
    request<{ items: Approval[] }>(
      `${workspacePath(workspaceId)}/runs/${encodeURIComponent(
        runId
      )}/approvals?limit=50`,
      "get",
      undefined,
      signal
    ),
  briefs: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: Brief[]; enabled: boolean }>(
      `${workspacePath(workspaceId)}/briefs?limit=50`,
      "get",
      undefined,
      signal
    ),
  brief: (workspaceId: string, briefId: string, signal?: AbortSignal) =>
    request<{ brief: Brief }>(
      `${workspacePath(workspaceId)}/briefs/${encodeURIComponent(briefId)}`,
      "get",
      undefined,
      signal
    ),
  generateBrief: (workspaceId: string, idempotencyKey: string) =>
    request<{ brief: Brief; replayed?: boolean }>(
      `${workspacePath(workspaceId)}/briefs`,
      "post",
      { idempotencyKey }
    ),
  issues: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: Issue[]; enabled: boolean }>(
      `${workspacePath(workspaceId)}/issues?limit=50`,
      "get",
      undefined,
      signal
    ),
  issueFeedback: (
    workspaceId: string,
    issue: Issue,
    action: IssueAction,
    idempotencyKey: string,
    snoozeUntil?: string
  ) =>
    request<{ issue: Issue; replayed?: boolean }>(
      `${workspacePath(workspaceId)}/issues/${encodeURIComponent(
        issue.id
      )}/feedback`,
      "post",
      {
        expectedRevision: issue.revision,
        idempotencyKey,
        action,
        ...(snoozeUntil ? { snoozeUntil } : {}),
      }
    ),
  monitoring: (workspaceId: string, signal?: AbortSignal) =>
    request<{ enabled: boolean; mission: MonitoringMission | null }>(
      `${workspacePath(workspaceId)}/monitoring`,
      "get",
      undefined,
      signal
    ),
  monitoringAction: (
    workspaceId: string,
    action: "start" | "pause" | "resume" | "check-now",
    idempotencyKey: string,
    missionVersion?: number
  ) =>
    request<{ mission: MonitoringMission; brief?: Brief; replayed?: boolean }>(
      `${workspacePath(workspaceId)}/monitoring/${action}`,
      "post",
      action === "start"
        ? { intervalSeconds: 21600, idempotencyKey }
        : { expectedMissionVersion: missionVersion, idempotencyKey }
    ),
  evaluation: (workspaceId: string, signal?: AbortSignal) => request<Evaluation>(`${workspacePath(workspaceId)}/evaluation`, "get", undefined, signal),
  evaluationFeedback: (workspaceId: string, payload: { targetType: "brief" | "issue"; targetId: string; value: string; idempotencyKey: string }) =>
    request<{ feedback: { value: string }; replayed: boolean }>(`${workspacePath(workspaceId)}/evaluation/feedback`, "post", payload),
  supportCandidates: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: SupportCandidate[]; enabled: boolean }>(
      `${workspacePath(workspaceId)}/support/candidates?limit=50`, "get", undefined, signal),
  refreshSupportCandidates: (workspaceId: string, idempotencyKey: string) =>
    request<{ brief: Brief; replayed?: boolean }>(
      `${workspacePath(workspaceId)}/support/candidates/refresh`, "post", { idempotencyKey }),
  supportCases: (workspaceId: string, signal?: AbortSignal) =>
    request<{ items: SupportCase[]; enabled: boolean }>(
      `${workspacePath(workspaceId)}/support/cases?limit=50`, "get", undefined, signal),
  supportAutomation: (workspaceId: string, signal?: AbortSignal) =>
    request<SupportAutomation>(
      `${workspacePath(workspaceId)}/support/automation`, "get", undefined, signal),
  supportCase: (workspaceId: string, caseId: string, signal?: AbortSignal) =>
    request<{ case: SupportCase }>(
      `${workspacePath(workspaceId)}/support/cases/${encodeURIComponent(caseId)}`, "get", undefined, signal),
  createSupportCase: (workspaceId: string, payload: { candidateId: string; customerId?: string; idempotencyKey: string }) =>
    request<{ case: SupportCase; replayed?: boolean }>(
      `${workspacePath(workspaceId)}/support/cases`, "post", payload),
  reviewSupportCase: (workspaceId: string, supportCase: SupportCase, payload: {
    action: SupportReviewAction; idempotencyKey: string; draftText?: string; reasonCode?: string;
  }) => request<{ case: SupportCase; replayed?: boolean }>(
    `${workspacePath(workspaceId)}/support/cases/${encodeURIComponent(supportCase.id)}/review`,
    "post", { expectedRevision: supportCase.revision, ...payload }),
  reinvestigateSupportCase: (workspaceId: string, supportCase: SupportCase, payload: {
    idempotencyKey: string; customerId?: string;
  }) => request<{ case: SupportCase; briefId: string; replayed: boolean }>(
    `${workspacePath(workspaceId)}/support/cases/${encodeURIComponent(supportCase.id)}/reinvestigate`,
    "post", { expectedRevision: supportCase.revision, ...payload }),
  previewSupportReproduction: (workspaceId: string, caseId: string, payload: ReproductionRequestPayload) =>
    request<ReproductionPreview>(
      `${workspacePath(workspaceId)}/support/cases/${encodeURIComponent(caseId)}/reproduction/preview`,
      "post", payload),
  approveSupportReproduction: (workspaceId: string, caseId: string, payload: ReproductionRequestPayload & { planHash: string }) =>
    request<{ id: string; status: string; approvedCaseRevision: number; limitations: string[] }>(
      `${workspacePath(workspaceId)}/support/cases/${encodeURIComponent(caseId)}/reproduction/approve`,
      "post", payload),
  fixtureRun: (workspaceId: string, agentId: string, idempotencyKey: string) =>
    request<{ run: Run }>(`${workspacePath(workspaceId)}/runs`, "post", {
      agentId,
      prompt:
        "Summarize fixture obligation TEST-1 and propose an internal note.",
      idempotencyKey,
    }),
  decide: (
    workspaceId: string,
    approval: Approval,
    decision: "approved" | "rejected"
  ) =>
    request<{ approval: Approval }>(
      `${workspacePath(workspaceId)}/runs/${encodeURIComponent(
        approval.runId
      )}/approvals/${encodeURIComponent(approval.id)}/decision`,
      "post",
      {
        decision,
        expectedVersion: approval.proposalVersion,
        expectedProposalHash: approval.proposalHash,
      }
    ),
};
